import * as React from "react";

/**
 * usePlatform — single source of truth for adaptive behaviour.
 *
 * Drives every platform-conditional decision (nav pattern, modal pattern,
 * iOS vs Material 3 motion / press feel). Reactive to resize and
 * orientationchange via a single shared listener.
 */

export type NavType = "bottom-tabs" | "sidebar" | "nav-rail";
export type ModalType = "bottom-sheet" | "popover" | "dialog";
export type PlatformId =
  | "ios"
  | "ipad"
  | "android-phone"
  | "android-tablet"
  | "desktop";

export interface PlatformInfo {
  // Device family
  isIOS: boolean;
  isIPad: boolean;
  isAndroidPhone: boolean;
  isAndroidTablet: boolean;
  // Form factor (resolved with width)
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  // Capability
  isPWA: boolean;
  hasPointer: boolean;
  prefersDark: boolean;
  prefersReducedMotion: boolean;
  // Orientation
  isLandscape: boolean;
  width: number;
  height: number;
  // Resolved patterns
  platform: PlatformId;
  navType: NavType;
  modalType: ModalType;
}

const PHONE_MAX = 767; // <= 767 is phone
const TABLET_MAX = 1024; // <= 1024 is tablet, > 1024 is desktop

function detectPlatform(): PlatformInfo {
  if (typeof window === "undefined") {
    return {
      isIOS: false, isIPad: false, isAndroidPhone: false, isAndroidTablet: false,
      isMobile: false, isTablet: false, isDesktop: true,
      isPWA: false, hasPointer: true, prefersDark: false, prefersReducedMotion: false,
      isLandscape: true, width: 1280, height: 800,
      platform: "desktop", navType: "sidebar", modalType: "dialog",
    };
  }

  const ua = navigator.userAgent;
  const w = window.innerWidth;
  const h = window.innerHeight;

  const isIOS = /iPhone|iPod/.test(ua);
  // Modern iPads report as MacIntel with touch points
  const isIPad =
    /iPad/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints ?? 0) > 1);
  const isAndroid = /Android/.test(ua);
  const isAndroidPhone = isAndroid && /Mobile/.test(ua);
  const isAndroidTablet = isAndroid && !/Mobile/.test(ua);

  // Form factor — width is the source of truth, UA is a hint
  const isMobile = w <= PHONE_MAX;
  const isTablet = !isMobile && w <= TABLET_MAX;
  const isDesktop = w > TABLET_MAX && !isIPad && !isAndroidTablet;

  const isPWA =
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  const hasPointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isLandscape = w > h;

  let platform: PlatformId = "desktop";
  if (isIOS) platform = "ios";
  else if (isIPad) platform = "ipad";
  else if (isAndroidPhone) platform = "android-phone";
  else if (isAndroidTablet) platform = "android-tablet";

  const navType: NavType = isMobile
    ? "bottom-tabs"
    : isIPad
    ? "sidebar"
    : isAndroidTablet
    ? "nav-rail"
    : isTablet
    ? "sidebar"
    : "sidebar";

  const modalType: ModalType = isMobile ? "bottom-sheet" : isTablet ? "popover" : "dialog";

  return {
    isIOS, isIPad, isAndroidPhone, isAndroidTablet,
    isMobile, isTablet, isDesktop,
    isPWA, hasPointer, prefersDark, prefersReducedMotion,
    isLandscape, width: w, height: h,
    platform, navType, modalType,
  };
}

// Module-level shared state so multiple components don't each spin up a listener.
let listeners = new Set<(p: PlatformInfo) => void>();
let current: PlatformInfo | null = null;
let initialized = false;

function ensureInitialized() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  current = detectPlatform();
  // Reflect on <html data-platform="…"> so CSS can target without JS.
  document.documentElement.dataset.platform = current.platform;
  document.documentElement.dataset.formFactor = current.isMobile
    ? "phone"
    : current.isTablet
    ? "tablet"
    : "desktop";

  let raf = 0;
  const recompute = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const next = detectPlatform();
      const changed =
        !current ||
        next.width !== current.width ||
        next.height !== current.height ||
        next.platform !== current.platform ||
        next.navType !== current.navType ||
        next.modalType !== current.modalType ||
        next.isLandscape !== current.isLandscape ||
        next.isPWA !== current.isPWA;
      current = next;
      if (changed) {
        document.documentElement.dataset.platform = next.platform;
        document.documentElement.dataset.formFactor = next.isMobile
          ? "phone"
          : next.isTablet
          ? "tablet"
          : "desktop";
        listeners.forEach((fn) => fn(next));
      }
    });
  };

  window.addEventListener("resize", recompute, { passive: true });
  window.addEventListener("orientationchange", recompute, { passive: true });
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener?.("change", recompute);
  window.matchMedia("(prefers-reduced-motion: reduce)").addEventListener?.("change", recompute);
  window.matchMedia("(display-mode: standalone)").addEventListener?.("change", recompute);
}

export function usePlatform(): PlatformInfo {
  ensureInitialized();
  const [state, setState] = React.useState<PlatformInfo>(() => current ?? detectPlatform());
  React.useEffect(() => {
    listeners.add(setState);
    // Sync once on mount in case state was created during SSR-ish render.
    if (current) setState(current);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return state;
}

// Convenience: get a snapshot outside React (e.g. event handlers, services).
export function getPlatformSnapshot(): PlatformInfo {
  ensureInitialized();
  return current ?? detectPlatform();
}
